import { Link, Navigate } from 'react-router-dom'

import {
  useDeletePerson,
  useMe,
  usePeople,
  useSetDisabled,
  useSetRole,
} from '../../hooks/queries'
import type { AccountRole, Person } from '../../api/types'
import { BackIcon } from '../../components/Icons'

/**
 * Wer darf eigene Reiseberichte schreiben.
 *
 * Sichtbar nur für die Verwaltung — für alle anderen wäre diese Liste ein
 * Verzeichnis aller Konten. Der Server verweigert sie ohnehin; hier wird sie
 * gar nicht erst angefragt.
 */
export function PeoplePage() {
  const me = useMe()
  const darfVerwalten = me.data?.may_manage_people === true
  const people = usePeople(darfVerwalten)
  const setRole = useSetRole()
  const setDisabled = useSetDisabled()
  const remove = useDeletePerson()

  if (me.data && !darfVerwalten) return <Navigate to="/settings" replace />

  return (
    <>
      <header className="bar">
        <Link className="icon-button" to="/settings" aria-label="Zurück">
          <BackIcon />
        </Link>
        <h2 className="bar__title bar__grow">Personen</h2>
      </header>

      <div className="scroll-panel">
        <div className="card" style={{ margin: '0 auto 16px', boxShadow: 'none' }}>
          <p className="card__lead" style={{ marginBottom: 0 }}>
            Wer über einen Einladungslink kommt, liest mit und antwortet. Zum
            Schreiben eines eigenen Reiseberichts gehört eine Freischaltung.
          </p>
        </div>

        {(setRole.isError || setDisabled.isError || remove.isError) && (
          <p className="notice" style={{ maxWidth: 520, margin: '0 auto 16px' }}>
            {(setRole.error ?? setDisabled.error ?? remove.error)?.message}
          </p>
        )}

        <div className="card" style={{ margin: '0 auto', boxShadow: 'none' }}>
          {people.isPending && <p className="card__lead">Wird geladen …</p>}

          <div className="stack">
            {people.data?.map((person) => (
              <PersonRow
                key={person.id}
                person={person}
                self={person.id === me.data?.id}
                busy={setRole.isPending || setDisabled.isPending || remove.isPending}
                onChange={(role) => setRole.mutate({ id: person.id, role })}
                onDisabled={(disabled) => setDisabled.mutate({ id: person.id, disabled })}
                onDelete={() => remove.mutate(person.id)}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

function PersonRow({
  person,
  self,
  busy,
  onChange,
  onDisabled,
  onDelete,
}: {
  person: Person
  self: boolean
  busy: boolean
  onChange: (role: AccountRole) => void
  onDisabled: (disabled: boolean) => void
  onDelete: () => void
}) {
  return (
    <div
      className="stack"
      style={{ gap: 6, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}
    >
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 600 }}>
          {person.display_name}
          {self && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · du</span>}
          {person.disabled && (
            <span style={{ color: 'var(--danger)', fontWeight: 400 }}> · stillgelegt</span>
          )}
        </span>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {person.trips > 0
            ? `${person.trips} ${person.trips === 1 ? 'Reise' : 'Reisen'}`
            : ''}
        </span>
      </div>

      <span style={{ fontSize: 13, color: 'var(--text-muted)', wordBreak: 'break-all' }}>
        {person.email}
      </span>

      {self ? (
        // Die eigene Rolle lässt sich nicht ändern -- sonst könnte sich die
        // letzte Verwaltung selbst aussperren.
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {beschreibung(person.role)} · Die eigene Rolle ändert eine zweite
          Verwaltung.
        </span>
      ) : (
        <>
          <label className="field" style={{ marginBottom: 0 }}>
            <span className="visually-hidden">Rolle von {person.display_name}</span>
            <select
              className="field__input"
              value={person.role}
              disabled={busy || person.disabled}
              onChange={(event) => onChange(event.target.value as AccountRole)}
            >
              <option value="reader">Liest mit</option>
              <option value="author">Darf Reisen anlegen</option>
              <option value="admin">Darf Reisen anlegen und verwalten</option>
            </select>
          </label>

          <button
            type="button"
            className="button button--outline"
            style={{ padding: '6px 12px', fontSize: 14 }}
            disabled={busy}
            onClick={() => {
              // Freigeben braucht keine Rückfrage, Stilllegen schon -- und die
              // Frage soll sagen, was es nicht tut: Reisen und Nachrichten
              // bleiben.
              if (
                person.disabled ||
                confirm(
                  `${person.display_name} stilllegen?\n\n` +
                    'Sie wird sofort abgemeldet und kommt nicht mehr herein. ' +
                    'Reisen, Nachrichten und Mitgliedschaften bleiben unberührt — ' +
                    'du kannst sie jederzeit wieder freigeben.',
                )
              ) {
                onDisabled(!person.disabled)
              }
            }}
          >
            {person.disabled ? 'Wieder freigeben' : 'Konto stilllegen'}
          </button>

          {/* Löschen nur, wo nichts dranhängt -- an einem Konto mit
              Nachrichten hinge ein fremder Reisebericht, aus dem hinterher
              Beiträge fehlten. Der Server prüft es noch einmal selbst; hier
              wird der Knopf gar nicht erst angeboten. */}
          {person.deletable && (
            <button
              type="button"
              className="button button--outline button--gefahr"
              style={{ padding: '6px 12px', fontSize: 14 }}
              disabled={busy}
              onClick={() => {
                if (
                  confirm(
                    `Konto von ${person.display_name} (${person.email}) endgültig löschen?\n\n` +
                      'An diesem Konto hängt nichts: keine Reise, keine Nachricht, ' +
                      'kein Foto. Das Löschen lässt sich nicht rückgängig machen.',
                  )
                ) {
                  onDelete()
                }
              }}
            >
              Konto löschen
            </button>
          )}
        </>
      )}
    </div>
  )
}

function beschreibung(role: AccountRole): string {
  if (role === 'admin') return 'Darf Reisen anlegen und verwalten'
  if (role === 'author') return 'Darf Reisen anlegen'
  return 'Liest mit'
}
